/**
 * How every timestamp in the interface is rendered, and the one switch that decides it.
 *
 * `UI-Design-Spec.md` line 120: *"Timestamps: **UTC by default with a toggle for local time**, and
 * the active mode always visible in the header. Mixed timezones is how people misread a trade
 * log."* The indicator was built; the toggle was not, and `AppShell` passed `timezone="UTC"` as a
 * hardcoded string.
 *
 * ## 🔴 UTC stays the default, and that is the spec rather than an accident
 *
 * A trade log read in the wrong zone is a wrong trade log, so the *default* is the zone the system
 * records in and the mode is on screen at all times. Local is an affordance for the operator
 * reading their own morning, not a new truth: **nothing here changes a stored value.** H5 keeps
 * every instant UTC in the database, in the vault and in `BUILD-LOG.md`.
 *
 * ⚠ **`AppTopBar` used to justify the hardcoded pill as *"Every time in this interface is UTC
 * (H5)"*, and that is a misreading worth naming.** H5 is `ensure_utc` refusing a naive datetime —
 * it governs what the *system* stores, not what a screen shows a human. The spec asks for the
 * toggle in the same document that states H5, so the two were never in tension.
 *
 * ## Why a module store rather than a context or a prop
 *
 * The switch lives in the header and the timestamps live in leaves — a Fleet row, a log line —
 * that reach the screen through react-router's `<Outlet />`, so no prop from `AppShell` can arrive.
 * A context would work and needs a provider wrapping the router; this needs neither, and
 * `useSyncExternalStore` is React 18's supported way to read exactly this shape.
 *
 * The alternative considered and rejected: a `data-` attribute on the root element, which is how
 * `theme` and `density` work. That is right for something **CSS** consumes and useless here —
 * the mode changes the text of a node, not its appearance.
 */

import { useSyncExternalStore } from 'react';

import { readTimestampMode, writeTimestampMode } from './preferences';

/** The two modes. `utc` is the product default — see the module note. */
export type TimestampMode = 'utc' | 'local';

/**
 * What the header pill reads in each mode.
 *
 * `UTC` is the spec's own word (the mock in `UI-Design-Spec.md` shows `[UTC]`). `Local` is spelled
 * out rather than resolved to an abbreviation: `CEST` is four characters of jargon that changes
 * twice a year, and a pill that silently became `CET` in October would read as a bug. The resolved
 * zone goes in the `title`, where it is available to anyone who wants it and cannot be misread as a
 * mode name.
 */
export const MODE_LABELS: Readonly<Record<TimestampMode, string>> = {
  utc: 'UTC',
  local: 'Local',
};

/** The browser's own zone, for the toggle's title. `undefined` where `Intl` cannot say. */
export function resolvedZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // `Intl` is present everywhere this app runs, but a formatter that throws inside a title
    // attribute would take a screen down for a label. There is nothing to report.
    return undefined;
  }
}

// --- the pure formatters -------------------------------------------------------------
//
// Both take the mode explicitly rather than reading the store, so they are testable without React
// and without a fake browser. The hook below is the only thing that knows about the store.

/** The calendar and clock parts of an instant, in the requested mode. */
function partsOf(when: Date, mode: TimestampMode) {
  return mode === 'utc'
    ? {
        year: when.getUTCFullYear(),
        month: when.getUTCMonth() + 1,
        day: when.getUTCDate(),
        hour: when.getUTCHours(),
        minute: when.getUTCMinutes(),
        second: when.getUTCSeconds(),
        ms: when.getUTCMilliseconds(),
      }
    : {
        // The local getters carry the offset *and* the DST rule for that specific instant, which
        // is why this is arithmetic-free: adding a fixed offset would be wrong for exactly the
        // timestamps that straddle a transition.
        year: when.getFullYear(),
        month: when.getMonth() + 1,
        day: when.getDate(),
        hour: when.getHours(),
        minute: when.getMinutes(),
        second: when.getSeconds(),
        ms: when.getMilliseconds(),
      };
}

const pad = (value: number, width = 2): string => String(value).padStart(width, '0');

/**
 * `14:05`, or `08-07 14:05` when the instant is not today. The Fleet screen's shape.
 *
 * ⚠ **"Today" is decided in the display mode**, not in UTC. Near midnight the two disagree — an
 * instant at `23:30Z` is *tomorrow* in Amsterdam — and comparing in UTC while rendering in local
 * would drop the date prefix from a row that is not from today, which is the lie of omission the
 * prefix exists to prevent.
 *
 * `now` is injectable so the boundary is testable without waiting for midnight.
 */
export function clockOf(iso: string, mode: TimestampMode, now: Date = new Date()): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return iso;
  const at = partsOf(when, mode);
  const today = partsOf(now, mode);
  const sameDay = at.year === today.year && at.month === today.month && at.day === today.day;
  const clock = `${pad(at.hour)}:${pad(at.minute)}`;
  return sameDay ? clock : `${pad(at.month)}-${pad(at.day)} ${clock}`;
}

/**
 * `12:34:56.789`. The log terminal's shape, where the milliseconds are the point — two lines in
 * the same second is the ordering question a log gets asked.
 */
export function preciseClockOf(iso: string, mode: TimestampMode): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return iso;
  const at = partsOf(when, mode);
  return `${pad(at.hour)}:${pad(at.minute)}:${pad(at.second)}.${pad(at.ms, 3)}`;
}

// --- the store -----------------------------------------------------------------------

let current: TimestampMode | null = null;
const listeners = new Set<() => void>();

/**
 * The active mode. Seeded from the stored preference on first read.
 *
 * Lazy rather than initialised at import: `readTimestampMode` touches `localStorage`, and a module
 * that reads storage at import time runs before any error boundary exists.
 */
export function timestampMode(): TimestampMode {
  current ??= readTimestampMode();
  return current;
}

/** Switch mode, persist it, and tell every subscriber. */
export function setTimestampMode(mode: TimestampMode): void {
  if (timestampMode() === mode) return;
  current = mode;
  writeTimestampMode(mode);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * The active mode, re-rendering the caller when it changes.
 *
 * Safe in a leaf: the header holds no authority over this and neither does any screen — there is
 * one value and everything reads it.
 */
export function useTimestampMode(): TimestampMode {
  return useSyncExternalStore(subscribe, timestampMode, timestampMode);
}

/** Test seam. Drops the cached mode and every subscriber, so a case starts from storage. */
export function resetTimestampModeForTests(): void {
  current = null;
  listeners.clear();
}

/**
 * Test seam for the notification half.
 *
 * Named `…ForTests` rather than exporting `subscribe` itself: the only production subscriber is
 * `useSyncExternalStore` inside this module, and a public `subscribe` would invite a second
 * subscription path that does not re-render anything.
 */
export function subscribeForTests(listener: () => void): () => void {
  return subscribe(listener);
}

/**
 * How long ago, as the coarsest unit that still says something — `4h`, `6d`, `just now`.
 *
 * 🔴 **S14 task 4.5 replaced an absolute clock with this, and the reason is what the column is
 * asked.** `Last in / out` rendered `13:01 / 12:13`, which answers *"at what time"* — a question
 * nobody asks of a six-week cohort. The question is *"is this candidate still trading"*, and that
 * is an age. The absolute instant is kept in `title`, so nothing is lost.
 *
 * ⚠ **No `Intl.RelativeTimeFormat`.** It renders *"4 hours ago"* — 11 characters where 2 will do,
 * in a column the same task narrowed from 196px to 78px. This is a table cell, not prose.
 *
 * ⚠ **Rounds DOWN, deliberately.** A position opened 119 minutes ago reads `1h`, never `2h`: this
 * column is read as *"at least this long"*, and rounding up would let a row claim more elapsed
 * evidence than it has.
 *
 * ⚠ **A future instant reads `just now` rather than a negative age.** Clock skew between this
 * browser and the server is real and small, and `−0h` on a fleet table would be read as a bug in
 * the fleet rather than as a bug in the clock.
 */
export function ageOf(iso: string, now: Date = new Date()): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return iso;
  const seconds = Math.floor((now.getTime() - when.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 9) return `${weeks}w`;
  // Beyond two months an age stops being the useful reading and the count of months is coarse
  // enough to be honest about. The cohort's own clock is six weeks, so this is the tail.
  return `${Math.floor(days / 30)}mo`;
}
